import { useEffect, useRef, useState } from "react";

import { useUiLanguage } from "./uiLanguageContext";
import { UI_LANGUAGES } from "./uiMessages";

export function UiLanguageMenu() {
  const { language, setLanguage, text } = useUiLanguage();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const current = UI_LANGUAGES.find(({ code }) => code === language) ?? UI_LANGUAGES[0];

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div className="ui-language-menu" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className="ui-language-trigger"
        aria-label={`${text("interfaceLanguage")}: ${current.displayName}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span
          className={`ui-language-flag ui-language-flag-${current.code}`}
          aria-hidden="true"
        />
        <span>{current.shortLabel}</span>
      </button>
      {open ? (
        <div className="ui-language-popover" role="menu" aria-label={text("interfaceLanguage")}>
          {UI_LANGUAGES.map((option) => (
            <button
              key={option.code}
              type="button"
              role="menuitemradio"
              aria-checked={option.code === language}
              className="ui-language-option"
              onClick={() => {
                setLanguage(option.code);
                setOpen(false);
                buttonRef.current?.focus();
              }}
            >
              <span
                className={`ui-language-flag ui-language-flag-${option.code}`}
                aria-hidden="true"
              />
              <span>{option.displayName}</span>
              <span className="ui-language-code">{option.shortLabel}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
